// Rug Room Shots: "AI check settings" (SPEC_AICHECK 1.4 / 2.3 / 4.12 / 5.8). Everyone with access sees the
// automatic-check switch and the Pass / Fail scores; only administrators and owners change them (the backend refuses
// anyone else with 403). PUT settings/ai-check takes only what changed: { autoCheck }, { passMin, failBelow } (both)
// or { resetThresholds: true } (back to the worker's defaults). Changing the scores re-labels every checked image at
// once: verdicts are computed from the stored score with the current scores, nothing is checked again or paid again.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, CircularProgress, Stack, Typography,
  FormControlLabel, Switch, TextField, Box, Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import rrsApi from './api';
import {
  primaryButtonSx, fmtUsd, fmtNy, errText, signalSpend, AI_NOT_READY_TEXT, aiNextPriceText,
} from './common';

const wholeNumber = (s) => (/^\d{1,3}$/.test(String(s).trim()) ? Number(String(s).trim()) : NaN);
export const validAiThresholds = (passMin, failBelow) => Number.isInteger(passMin) && Number.isInteger(failBelow)
  && failBelow >= 0 && failBelow < passMin && passMin <= 100;

// The three bands on a 0-100 bar: Fail [0, failBelow), Needs a look [failBelow, passMin), Pass [passMin, 100].
const BandBar = ({ passMin, failBelow }) => {
  const theme = useTheme();
  if (!validAiThresholds(passMin, failBelow)) return null;
  const seg = (from, to, color, label) => {
    const w = Math.max(0, to - from);
    if (!w) return null;
    return (
      <Tooltip key={label} title={`${label}: ${from} to ${to === 101 ? 100 : to - 1}`}>
        <Box
          sx={{
            flex: `${w} 0 0`, minWidth: 0, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.35 : 0.2), color: 'text.primary',
            borderRight: `1px solid ${theme.palette.background.paper}`,
          }}
        >
          <Typography variant="caption" noWrap sx={{ px: 0.5, fontWeight: 600 }}>{label}</Typography>
        </Box>
      </Tooltip>
    );
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
        {seg(0, failBelow, theme.palette.error.main, 'Fail')}
        {seg(failBelow, passMin, theme.palette.warning.main, 'Needs a look')}
        {seg(passMin, 101, theme.palette.success.main, 'Pass')}
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25 }}>
        <Typography variant="caption" color="text.secondary">0</Typography>
        <Typography variant="caption" color="text.secondary">100</Typography>
      </Stack>
    </Box>
  );
};

const changedBy = (email, at) => {
  if (!at && !email) return '';
  return `Last changed${at ? ` ${fmtNy(at)}` : ''}${email ? ` by ${email}` : ''}.`;
};

const AiCheckSettingsDialog = ({ open, onClose, notify, onSaved }) => {
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [autoCheck, setAutoCheck] = useState(true);
  const [passText, setPassText] = useState('');
  const [failText, setFailText] = useState('');
  const [reset, setReset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const apply = (s) => {
    setSettings(s);
    setAutoCheck(s.autoCheck !== false);
    setPassText(String(s.passMin ?? ''));
    setFailText(String(s.failBelow ?? ''));
    setReset(false);
  };

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const s = await rrsApi.getAiCheckSettings();
      apply(s || {});
    } catch (e) {
      setLoadError(e && e.body && e.body.error && e.status !== 403 ? e.body.error : errText(e));
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const s = settings || {};
  const canEdit = !!s.canEdit;
  const defaults = s.defaults || {};
  const passMin = wholeNumber(passText);
  const failBelow = wholeNumber(failText);
  const valid = validAiThresholds(passMin, failBelow);
  const autoChanged = !!settings && autoCheck !== (s.autoCheck !== false);
  const thresholdsChanged = !!settings && !reset && valid && (passMin !== s.passMin || failBelow !== s.failBelow);
  const resetPending = !!settings && reset && !!s.thresholdsStored;
  const anyChange = autoChanged || thresholdsChanged || resetPending;
  const canSave = canEdit && !saving && anyChange && (reset || valid);

  const onReset = () => {
    setPassText(String(defaults.passMin ?? ''));
    setFailText(String(defaults.failBelow ?? ''));
    setReset(true);
  };

  const save = async () => {
    if (!canSave) return;
    const body = {};
    if (autoChanged) body.autoCheck = autoCheck;
    if (resetPending) body.resetThresholds = true;
    else if (thresholdsChanged) { body.passMin = passMin; body.failBelow = failBelow; }
    setSaving(true);
    setSaveError('');
    try {
      const res = await rrsApi.saveAiCheckSettings(body);
      if (res && typeof res === 'object' && res.passMin !== undefined) apply(res);
      notify('AI check settings saved.', 'success');
      signalSpend(); // re-reads /config (its aiCheck block carries the switch and the scores)
      if (onSaved) onSaved(res);
      onClose();
    } catch (e) {
      // 403 "Only administrators can change the AI check settings." and the 400s: the server's own words.
      setSaveError(e && e.body && e.body.error ? e.body.error : errText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>AI check settings</DialogTitle>
      <DialogContent dividers>
        {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
        {!settings && !loadError && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {settings && (
          <>
            {s.workerReady === false && <Alert severity="info" sx={{ mb: 1.5 }}>{AI_NOT_READY_TEXT}</Alert>}
            {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>Only administrators can change these.</Alert>}
            {saveError && <Alert severity="error" sx={{ mb: 1.5 }}>{saveError}</Alert>}

            <FormControlLabel
              control={<Switch checked={autoCheck} onChange={(e) => setAutoCheck(e.target.checked)} disabled={!canEdit || saving} />}
              label="Check new images automatically"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: { xs: 0, sm: 6 } }}>
              On: every new image gets an AI check right after it is made (about {fmtUsd(s.usdPerCheck, 3)} each). Off: new
              images are not checked; Check with AI… still checks the images you pick. Checks already waiting still run:
              Stop queued checks on the Review tab drops a job&apos;s waiting checks.
              {' '}{changedBy(s.autoUpdatedByEmail, s.autoUpdatedAt)}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5 }}>Scores</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} sx={{ mt: 1 }}>
              <TextField
                size="small" type="number" label="Pass at or above" value={passText}
                onChange={(e) => { setPassText(e.target.value.slice(0, 3)); setReset(false); }}
                inputProps={{ min: 1, max: 100, step: 1 }} disabled={!canEdit || saving} sx={{ width: 170 }}
                error={!reset && passText !== '' && !Number.isInteger(passMin)}
              />
              <TextField
                size="small" type="number" label="Fail below" value={failText}
                onChange={(e) => { setFailText(e.target.value.slice(0, 3)); setReset(false); }}
                inputProps={{ min: 0, max: 99, step: 1 }} disabled={!canEdit || saving} sx={{ width: 170 }}
                error={!reset && failText !== '' && !Number.isInteger(failBelow)}
              />
              {canEdit && (
                <Button
                  size="small" onClick={onReset} disabled={saving}
                  sx={{ alignSelf: { sm: 'center' } }}
                >
                  Reset to defaults ({defaults.passMin ?? '?'} / {defaults.failBelow ?? '?'})
                </Button>
              )}
            </Stack>
            {!valid && (passText !== '' || failText !== '') && (
              <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                Whole numbers with 0 ≤ Fail below &lt; Pass at or above ≤ 100.
              </Typography>
            )}
            <BandBar passMin={passMin} failBelow={failBelow} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Between the two: Needs a look. A hard fail (rug missing, different design, wrong shape, room changed, extra
              rug, rug cut off) is always Fail, and a major problem is never a Pass. Changing these re-labels every
              checked image at once; nothing is checked again.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {s.thresholdsStored ? 'Set on this page.' : 'The defaults are in use.'}
              {' '}{changedBy(s.thresholdsUpdatedByEmail, s.thresholdsUpdatedAt)}
              {reset && s.thresholdsStored ? ' Saving goes back to the defaults.' : ''}
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Model {s.model || '-'} · prompt {s.promptVersion || '-'} · about {fmtUsd(s.usdPerCheck, 3)} per image
              {aiNextPriceText(s.usdPerCheckNext) ? ` (${aiNextPriceText(s.usdPerCheckNext)}: Google's new price)` : ''}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{canEdit ? 'Cancel' : 'Close'}</Button>
        {canEdit && (
          <Button variant="contained" onClick={save} disabled={!canSave} sx={primaryButtonSx}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiCheckSettingsDialog;
